// External dependencies
import { FC, useState } from 'react';

// Utilities
import { _isNotBlank, _notNil, safelyDecodeURIComponent } from '@/littledash';

// Model types
import type { FetchFromApiConfig } from '@/components/UI/Table/TableComponent.model';

// Support modules
import { ApiService } from '@/support/ApiService';
import { LegacyApiService } from '@/support/LegacyApiService';

// Internal components
import Lookup from '@/components/UI/Lookup';
import { EndpointParameters } from '@/generated/internal-api/LegacyApiRoutes';
import { Endpoints } from '@/generated/internal-api/Endpoints';

interface FilterLookupProps {
  fetchFromApi: FetchFromApiConfig;
  value: {
    name: string;
    value: string;
  };
  onSelect: (data: { label: string; value: string }) => void;
}

interface PaginationMeta {
  current_page?: number;
  total_pages?: number;
  pagination?: {
    current_page?: number;
    total_pages?: number;
  };
}

interface OptionItem {
  name?: string;
  title?: string;
  id?: string | number;
  [key: string]: unknown;
}

type ApiData = Array<OptionItem> | { options?: Array<string> } | null;

/**
 * FilterLookup - isolated Lookup component with its own loading state
 * to prevent parent re-renders from causing focus loss
 */
const FilterLookup: FC<FilterLookupProps> = ({ fetchFromApi, value, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);

  const fetchData = async (query: string, page: number): Promise<{ data: ApiData; meta: PaginationMeta | null }> => {
    const queryParams = {
      page,
      ...fetchFromApi.queryParams,
      ...(_isNotBlank(query) ? { query } : {}),
    };

    if (fetchFromApi.useModernApi === true) {
      const response = await ApiService.call({
        endpoint: fetchFromApi.url as keyof Endpoints,
        path: fetchFromApi.params as never,
        query: queryParams as never,
      });
      return { data: response.body as ApiData, meta: null };
    }

    const response = await LegacyApiService.call({
      method: 'GET',
      apiRoute: fetchFromApi.url as keyof EndpointParameters,
      path: fetchFromApi.params as never,
      query: queryParams,
    });
    const body = response.body as { data: ApiData; meta: PaginationMeta };
    return { data: body.data, meta: body.meta };
  };

  const calculateHasMore = (meta: PaginationMeta | null): boolean => {
    if (!_notNil(meta)) {
      return false;
    }
    const pagination = meta.pagination ?? meta;
    return (pagination.current_page ?? 0) < (pagination.total_pages ?? 0);
  };

  const mapOptionsFromData = (data: ApiData): Array<{ label: string; value: string }> => {
    const { fieldType, tableFilterMenuProperty } = fetchFromApi;

    if (fieldType === 'select' || fieldType === 'multi_select') {
      const decode = fieldType === 'multi_select' ? safelyDecodeURIComponent : (x: string) => x;
      const dataWithOptions = data as { options?: Array<string> } | null;
      const rawOptions = dataWithOptions?.options ?? [];
      return rawOptions.map((d: string) => ({
        label: decode(d),
        value: d,
      }));
    }

    const dataArray = (data ?? []) as Array<OptionItem>;
    return dataArray.map((d: OptionItem) => ({
      label: safelyDecodeURIComponent(d.name ?? d.title ?? ''),
      value: String(d[tableFilterMenuProperty ?? 'id'] ?? ''),
    }));
  };

  const loadOptions = async ({ inputValue: query, page }: { inputValue: string; page: number }) => {
    setLoading(true);
    setApiError(false);

    try {
      const { data, meta } = await fetchData(query, page);

      return {
        options: mapOptionsFromData(data),
        hasMore: calculateHasMore(meta),
      };
    } catch {
      setApiError(true);
      // Note: ApiService/LegacyApiService already capture exceptions to Sentry
      return { options: [], hasMore: false };
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-100">
      <Lookup
        isLoading={loading}
        isMulti={false}
        placeholder={fetchFromApi.placeholder}
        debounceTimeout={300}
        loadOptions={loadOptions}
        value={{
          label: value.name,
          value: value.value,
        }}
        handleSelect={onSelect}
      />
      {apiError && <p className="f7 red">There was a problem fetching filter options</p>}
    </div>
  );
};

export default FilterLookup;
